import Backdrop from '@components/v2/ui/Backdrop/Backdrop';
import { Box, useTheme } from '@mui/material';
import { type ReactNode } from 'react';
import { CONTENT_FRAME_ID } from './appTemplate.consts';
import { useAppTemplateStyles } from './appTemplate.styles';
import { CollapsedMode, ContextMode, NavSection } from './appTemplate.types';
import { hasVisibleNavItems } from './appTemplate.utils';
import BottomBar from './components/BottomBar/BottomBar';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import { WorkspaceMassActionsSlot } from './components/WorkspaceMassActionsSlot/WorkspaceMassActionsSlot';
import { AppTemplateContext } from './contexts/AppTemplate.context';
import { useAppTemplate } from './hooks/useAppTemplate';
import { useTopSlotHeight } from './hooks/useTopSlotHeight';

interface AppTemplateSlots {
  topSlot?: ReactNode;
  header?: ReactNode;
  bottomBar?: ReactNode;
  sidebarBottom?: ReactNode;
  subContextTop?: ReactNode;
  workspaceOverlay?: ReactNode;
}

interface AppTemplateProps {
  navItems: NavSection[];
  subContextNavItems?: NavSection[];
  collapsedMode: CollapsedMode;
  contextMode?: ContextMode;
  onBack?: () => void;
  slots?: AppTemplateSlots;
  children: ReactNode;
}

const AppTemplate = ({
  navItems,
  subContextNavItems = [],
  collapsedMode,
  contextMode = 'root',
  onBack,
  slots,
  children,
}: AppTemplateProps) => {
  const {
    effectiveSidebarState,
    isBlurring,
    handleMenuToggle,
    closeMobileSidebar,
    isMobile,
    isMobileOpen,
  } = useAppTemplate({ collapsedMode, contextMode, onBack });
  const { topSlotRef, topSlotHeight } = useTopSlotHeight();
  const theme = useTheme();
  const { classes, cx } = useAppTemplateStyles({ sidebarState: effectiveSidebarState, isMobile });

  const hasSidebar =
    hasVisibleNavItems(navItems) ||
    hasVisibleNavItems(subContextNavItems) ||
    !!slots?.sidebarBottom ||
    !!slots?.subContextTop;

  return (
    <AppTemplateContext.Provider value={{ sidebarState: effectiveSidebarState, topSlotHeight }}>
      <Box className={classes.root}>
        {slots?.topSlot && (
          <Box ref={topSlotRef} sx={{ flexShrink: 0 }}>
            {slots.topSlot}
          </Box>
        )}
        <Header
          contextMode={contextMode}
          isMobile={isMobile}
          isMenuExpanded={isMobile ? isMobileOpen : effectiveSidebarState === 'expanded'}
          showMenuToggle={hasSidebar}
          onMenuToggle={handleMenuToggle}
        >
          {slots?.header}
        </Header>
        <Box className={classes.middle}>
          {hasSidebar && (
            <Sidebar
              navItems={navItems}
              subContextNavItems={subContextNavItems}
              bottomSlot={slots?.sidebarBottom}
              subContextTopSlot={slots?.subContextTop}
              sidebarState={effectiveSidebarState}
              contextMode={contextMode}
              topSlotHeight={topSlotHeight}
              className={cx(classes.sidebarWrapper, isBlurring && classes.sidebarBlurring)}
              mobile={{
                isMobile,
                isOpen: isMobileOpen,
                onClose: closeMobileSidebar,
              }}
              onBack={onBack}
            />
          )}
          <Box className={classes.workspaceArea}>
            <Box className={classes.contentArea}>
              <Box component="main" id={CONTENT_FRAME_ID} className={classes.contentFrame}>
                <WorkspaceMassActionsSlot />
                {children}
              </Box>
            </Box>
            {slots?.workspaceOverlay}
          </Box>
        </Box>
        {slots?.bottomBar && <BottomBar>{slots.bottomBar}</BottomBar>}
        {hasSidebar && isMobile && (
          <Backdrop open={isMobileOpen} zIndex={theme.zIndex.drawer - 1} />
        )}
      </Box>
    </AppTemplateContext.Provider>
  );
};

export default AppTemplate;
